import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { getAsset } from '../../../../../../../../../util/assets';
import Badge from '../../../../../../../../../components/global/Badge';
import PriceDisplay from '../../../../../../../../../components/global/PriceDisplay';
import Button from '../../../../../../../../../components/global/Button';
import AddedToCartLabel from '../../../PhotoPurchasingOptions/AddedToCartLabel';
import { isAlbumCustomPrintConfigurableInPlace } from '../../helpers';
import {
  CURRENCY,
  CURRENCY_SYMBOLS,
} from '../../../../../../../../../constants/currency';
import {
  getAlbumCustomPrintName,
  getAlbumCustomPrintPrice,
} from '../../../../../../../../../util/custom-prints';
import AlbumCustomPrintPreviewModal from './AlbumCustomPrintPreviewModal';
import ImagePreload from '../../../../../../../../../components/global/ImagePreload';
import { validateCustomPrintPhotoSelections } from '../../../../../../../../../components/global/CustomPrintConfigurator/CustomPrintPlaqueConfigurator/helpers';
import CustomPrintPlaqueConfiguratorModal from '../../../../../../../../../components/global/CustomPrintConfigurator/CustomPrintPlaqueConfigurator/modal';
import { formatPriceForDisplay } from '../../../../../../../../../util/format-money';

const propTypes = {
  className: PropTypes.string,
  readOnly: PropTypes.bool,
  albumCustomPrint: PropTypes.shape({
    id: PropTypes.string.isRequired,
    price: PropTypes.shape({
      value: PropTypes.number,
      currency: PropTypes.oneOf(Object.values(CURRENCY)),
    }),
    discount_price: PropTypes.shape({
      value: PropTypes.number,
      currency: PropTypes.oneOf(Object.values(CURRENCY)),
    }),
  }).isRequired,
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  cartItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onAlbumCustomPrintToggle: PropTypes.func.isRequired,
  hasPurchasedSubscription: PropTypes.bool,
  hasSubscriptionInCart: PropTypes.bool,
  subscriptionAvailable: PropTypes.bool,
};

const AlbumCustomPrintOption = ({
  className,
  readOnly,
  albumCustomPrint,
  identity,
  cartItem,
  onAlbumCustomPrintToggle,
  hasPurchasedSubscription,
  hasSubscriptionInCart,
  subscriptionAvailable,
}) => {
  const classes = classNames({
    [className]: !!className,
    'purchasing-option-item': true,
    'purchasing-option-item-album-custom-print': true,
  });
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [configurationModalOpen, setConfigurationModalOpen] = useState(false);
  const name = getAlbumCustomPrintName(albumCustomPrint);
  const price = getAlbumCustomPrintPrice(albumCustomPrint);
  const discountPrice = getAlbumCustomPrintPrice(albumCustomPrint, true);
  const currency =
    albumCustomPrint?.discount_price?.currency ??
    albumCustomPrint?.price?.currency ??
    CURRENCY.USD;
  const hasPreviewImage = !!albumCustomPrint.custom_print.preview_image;
  const shouldDiscount = hasSubscriptionInCart;
  const albumCustomPrintConfigurableInPlace =
    isAlbumCustomPrintConfigurableInPlace(albumCustomPrint);

  const onAlbumCustomPrintRequest = () => {
    if (albumCustomPrintConfigurableInPlace && !cartItem) {
      setConfigurationModalOpen(true);
      return;
    }

    onAlbumCustomPrintToggle();
  };

  return (
    <div className={classes}>
      <div>
        <div className="purchasing-option-item-info-extra">
          <Badge
            badgeStyle="yellow"
            className="purchasing-option-item-info-badge"
          >
            <PriceDisplay
              title="Custom print price"
              price={shouldDiscount && !!discountPrice ? discountPrice : price}
              oldPrice={price}
              showDiscount={shouldDiscount}
              animatedStrikethrough
              trimDecimalZeros={false}
            />
          </Badge>
        </div>

        <div className="purchasing-option-item-thumb">
          <img width={75} src={getAsset('/plaque.svg')} alt="" />
        </div>
      </div>

      <div className="purchasing-option-item-content-wrap">
        <div className="purchasing-option-item-title">{name}</div>

        <div className="purchasing-option-item-content mb-2">
          <p className="text-sm lh-normal text-dark text-bold">
            Pick your fav pics and build your own custom print!
          </p>

          {hasPreviewImage && (
            <p className="mb-0">
              <Button
                btnStyle="link"
                className="text-sm lh-normal text-extra-bold"
                onClick={() => setPreviewModalOpen(true)}
              >
                See sample
              </Button>
            </p>
          )}

          {discountPrice > 0 && subscriptionAvailable && (
            <p className="d-block text-sm text-italic text-bold">
              Only{' '}
              <span className="text-primary">
                {CURRENCY_SYMBOLS[currency]}
                {formatPriceForDisplay(discountPrice, true)}
              </span>{' '}
              if purchased with a Digital Photo Pass. Discount is limited to 1
              print.
            </p>
          )}
        </div>

        {!readOnly && (
          <Button
            btnStyle={classNames({
              primary: !cartItem,
              info: !!cartItem,
            })}
            className={classNames({
              'purchasing-options-item-btn': true,
              'btn-purchase-options-in-cart': !!cartItem,
            })}
            onClick={onAlbumCustomPrintRequest}
            data-testid="btn-album-custom-print"
          >
            {cartItem ? (
              <AddedToCartLabel />
            ) : (
              <Fragment>
                {albumCustomPrintConfigurableInPlace
                  ? 'Select photos'
                  : 'Add to cart'}
              </Fragment>
            )}
          </Button>
        )}
      </div>

      {hasPreviewImage && (
        <Fragment>
          <AlbumCustomPrintPreviewModal
            forceOpen={previewModalOpen}
            onClose={() => setPreviewModalOpen(false)}
            albumCustomPrint={albumCustomPrint}
            onAlbumCustomPrintToggle={onAlbumCustomPrintRequest}
            shouldDiscount={shouldDiscount}
            albumCustomPrintInCart={!!cartItem}
            hasPurchasedSubscription={hasPurchasedSubscription}
          />
          <ImagePreload
            urls={[albumCustomPrint.custom_print.preview_image.url]}
          />
        </Fragment>
      )}

      <CustomPrintPlaqueConfiguratorModal
        forceOpen={configurationModalOpen}
        albumCustomPrint={albumCustomPrint}
        identity={identity}
        renderSubmit={config => {
          return (
            <Fragment>
              {validateCustomPrintPhotoSelections(config) && (
                <Button
                  btnStyle="yellow"
                  btnSize="lg"
                  btnBlock
                  onClick={() => {
                    setConfigurationModalOpen(false);
                    onAlbumCustomPrintToggle(config);
                  }}
                >
                  <span className="pointer-events-none">
                    <Fragment>
                      Add to cart &ndash;{' '}
                      <PriceDisplay
                        price={
                          shouldDiscount && !!discountPrice
                            ? discountPrice
                            : price
                        }
                        oldPrice={price}
                        showDiscount={shouldDiscount}
                        animatedStrikethrough
                      />
                    </Fragment>
                  </span>
                </Button>
              )}

              <Button
                btnStyle="link"
                btnBlock
                onClick={() => setConfigurationModalOpen(false)}
                className="text-center mt-2"
              >
                Dismiss
              </Button>
            </Fragment>
          );
        }}
      />
    </div>
  );
};

AlbumCustomPrintOption.propTypes = propTypes;

export default AlbumCustomPrintOption;
